import React, { useState, useEffect } from 'react';
import { FileText, CheckCircle } from 'lucide-react';
import { ApplicationFormData, ApplicationFormErrors } from '@/types/application';
import { agentApi } from '@/services/agentApi';

interface InstructionsStepProps {
  formData: ApplicationFormData;
  errors: ApplicationFormErrors;
  updateFormData: (updates: Partial<ApplicationFormData>) => void;
  setErrors: (errors: ApplicationFormErrors) => void;
}

export const InstructionsStep: React.FC<InstructionsStepProps> = ({
  formData,
}) => {
  const [institutionName, setInstitutionName] = useState<string>('');

  useEffect(() => {
    if (formData.universityId) {
      loadInstitutionInfo();
    } else {
      setInstitutionName('');
    }
  }, [formData.universityId]);

  const loadInstitutionInfo = async () => {
    if (!formData.universityId) return;
    
    try {
      const targetId = Number(formData.universityId);
      
      // Try direct API call first
      try {
        const university = await agentApi.getUniversityById(targetId);
        if (university && university.name) {
          setInstitutionName(university.name);
          return;
        }
      } catch (err) {
        console.warn('Error fetching university directly:', err);
      }
      
      // Fallback: try approved institutions
      try {
        const institutions = await agentApi.getApprovedInstitutions();
        const institution = institutions.find(inst => {
          const instId = Number(inst.id);
          return instId === targetId;
        });
        
        if (institution && institution.name) {
          setInstitutionName(institution.name);
          return;
        }
      } catch (err) {
        console.warn('Error loading approved institutions:', err);
      }
      
      // If all else fails, use a default
      setInstitutionName('this institution');
    } catch (error) {
      console.error('Error loading institution info:', error);
      setInstitutionName('this institution');
    }
  };

  return (
    <div>
      <div className="card border-0 shadow-sm mb-4">
        <div className="card-body p-4">
          <div className="d-flex align-items-center mb-3">
            <FileText size={24} className="text-primary me-3" />
            <h5 className="fw-bold mb-0">
              Welcome to {institutionName || 'this institution'}
            </h5>
          </div>
          
          <p className="mb-3">
          Please read the instructions below carefully before completing your application. 
          </p> 
          <p className="mb-3">
          This portal is designed to collect all the required information needed to process your enrolment into a {institutionName || 'this institution'} programme. The details you provide will also be used to meet regulatory
requirements set by the Ministry of Education and other relevant government bodies in New Zealand for
registration and reporting purposes.          </p>

          <div className="bg-light p-3 rounded mb-3">
            {/* <h6 className="fw-semibold mb-3">Instructions</h6> */}
            <p className="mb-3">
            To ensure a smooth and timely application process, please:
            </p>
            <ul className="mb-3 mt-2">
              <li>Complete all required sections accurately</li>
              <li>Provide clear and correct information</li>
              <li>Upload all necessary supporting documents</li>
              <li>Review your application before submission</li>
            </ul>
            <p className="mb-3">
            Once submitted, your application will be reviewed, and you will be contacted with the next steps. You
may also save your progress and return at any time before final submission.
            </p>
          </div>
          <p className="mb-3">
          We look forward to supporting you on your journey with Kalandra Education Group.
          </p>
        </div>
      </div>

      {!formData.universityId && (
        <div className="alert alert-warning">
          <strong>No institution selected.</strong> Please go back to step 1 to select an institution first.
        </div>
      )}

      {formData.universityId && institutionName && (
        <div className="alert alert-info d-flex align-items-center">
          <CheckCircle size={20} className="me-2" />
          <span>You are applying to <strong>{institutionName}</strong></span>
        </div>
      )}
    </div>
  );
};

